import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as pricingPlanActions from '@redux/modules/pricingPlan'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

import defaultFormState, { requiredFields } from '@models/pricingPlan'
import type { PricingPlanModel } from '@models/types'

const stringifyObjectKeys: (keyof PricingPlanModel)[] = ['data']

export const generateUrls = () => ({
  pricingPlansIndexUrl: '#/systemManager/pricingPlans',
})

type CreatePricingPlanParams = {
  dispatch: AppDispatch,
  pricingPlanParams: Partial<PricingPlanModel>,
  requestOptions?: EntityOptions,
}

const createPricingPlan = (params: CreatePricingPlanParams) => {
  const { dispatch, pricingPlanParams, requestOptions } = params
  const { createPricingPlan: createFn } = pricingPlanActions

  const updatedParams = mergeAndStringifyEntityObjects({}, pricingPlanParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdatePricingPlanParams = {
  dispatch: AppDispatch,
  pricingPlan: PricingPlanModel,
  pricingPlanParams: Partial<PricingPlanModel>,
  requestOptions?: EntityOptions,
}

const updatePricingPlan = (params: UpdatePricingPlanParams) => {
  const {
    dispatch, pricingPlan, pricingPlanParams, requestOptions,
  } = params
  const { updatePricingPlan: updateFn } = pricingPlanActions

  const updatedParams = mergeAndStringifyEntityObjects(pricingPlan, pricingPlanParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CreateOrEditPricingPlanModalParams = {
  pricingPlan: PricingPlanModel,
  showCreateOrEditPricingPlanModal: Function,
}

const createOrEditPricingPlan = (params: CreateOrEditPricingPlanModalParams) => (
  new Promise((resolve, reject) => {
    const { pricingPlan, showCreateOrEditPricingPlanModal } = params

    if (showCreateOrEditPricingPlanModal){
      const payload = {
        pricingPlan,
      }

      showCreateOrEditPricingPlanModal(payload)

      resolve({ success: true, result: {} })
    }

    reject(new Error('showCreateOrEditPricingPlanModal not defined in PageContext callbacks'))
  })
)

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePricingPlanForm(
  pricingPlan: Partial<PricingPlanModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const pricingPlanForm = useForm(
    defaultFormState,
    { entity: pricingPlan, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [pricingPlan?.id, pricingPlan?.cache_key],
  )

  return {
    ...pricingPlanForm,
  }
}

function usePricingPlan(initEntity: Partial<PricingPlanModel> = {}) {
  const { entity: pricingPlan } = useLatestEntity(initEntity, 'pricingPlans')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditPricingPlanModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.pricingPlans)

  return {
    callbacks: {
      // @ts-expect-error TS2322
      createOrEditPricingPlan: () => createOrEditPricingPlan({ pricingPlan, showCreateOrEditPricingPlanModal }),
      createPricingPlan: (pricingPlanParams: Partial<PricingPlanModel>) => (
        createPricingPlan({ pricingPlanParams, dispatch })
      ),
      updatePricingPlan: (pricingPlanParams: Partial<PricingPlanModel>, requestOptions: EntityOptions) => (
        updatePricingPlan({
          // @ts-expect-error TS2322
          pricingPlan, pricingPlanParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    pricingPlan,
    updating,
  }
}

export default usePricingPlan
